import { useState } from "react";
import Stepper from "./stepper";
import Step1Form from "./step1";
import Step2Form from "./step2";
import Step3Form from "./step3";
import Step4Form from "./step4";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { object, string, array, mixed, boolean } from "yup";

const BeOurAgent = () => {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [successPopup, setSuccessPopup] = useState(false);

  // ================= VALIDATION =================
  const validationSchema = object({
    companyName: string().required(t("requiredField")),
    fullAddress: string().required(t("requiredField")),
    city: string().required(t("requiredField")),
    phone: string().required(t("requiredField")),
    email: string().email(t("invalidEmail")).required(t("requiredField")),

    fullName: string().required(t("requiredField")),
    jobPosition: string().required(t("requiredField")),
    personalPhone: string().required(t("requiredField")),
    personalEmail: string().email(t("invalidEmail")).required(t("requiredField")),

    experience: string().required(t("requiredField")),

    shippingType: array().when("experience", ([experience], schema) => {
      if (experience === "yes") {
        return schema.min(1, t("requiredField"));
      }
      return schema;
    }),

    otherText: string().when(
      ["experience", "shippingType"],
      ([experience, shippingType], schema) => {
        if (experience === "yes" && shippingType?.includes("other")) {
          return schema.required(t("requiredField"));
        }
        return schema;
      }
    ),

    countriesYourCompanyCovers: string().when(
      "experience",
      ([experience], schema) => {
        if (experience === "yes") {
          return schema.required(t("requiredField"));
        }
        return schema;
      }
    ),

    yearsOfExperienceInTheField: string().when(
      "experience",
      ([experience], schema) => {
        if (experience === "yes") {
          return schema.required(t("requiredField"));
        }
        return schema;
      }
    ),

    commercialRegisterCopy: mixed().required(t("requiredField")),
    professionalLicenseCopy: mixed().required(t("requiredField")),
    otherSupportiveDocuments: mixed().required(t("requiredField")),

    iCertify: boolean().oneOf([true], t("requiredField")),
    applicantName: string().required(t("requiredField")),
  });

  // ================= FORM =================
  const formik = useFormik({
    initialValues: {
      companyName: "",
      fullAddress: "",
      city: "",
      phone: "",
      email: "",

      fullName: "",
      jobPosition: "",
      personalPhone: "",
      personalEmail: "",

      experience: "",
      shippingType: [],
      otherText: "",

      countriesYourCompanyCovers: "",
      yearsOfExperienceInTheField: "",

      commercialRegisterCopy: null,
      professionalLicenseCopy: null,
      otherSupportiveDocuments: null,

      iCertify: false,
      applicantName: "",
    },

    validationSchema,

    // ================= API SUBMIT =================
    onSubmit: async (values) => {
      try {
        setLoading(true);

        const formData = new FormData();

        // Step 1
        formData.append("company_address", values.fullAddress);
        formData.append("city", values.city);
        formData.append("phone", values.phone);
        formData.append("email", values.email);

        // Step 2
        formData.append("legal_name", values.fullName);
        formData.append("job_title", values.jobPosition);
        formData.append("personal_phone", values.personalPhone);
        formData.append("personal_email", values.personalEmail);

        // Step 3
        formData.append(
          "has_shipping_experience",
          values.experience === "yes" ? 1 : 0
        );

        values.shippingType?.forEach((item, i) => {
          formData.append(`shipping_types[${i}]`, item);
        });

        formData.append(
          "covered_countries",
          values.countriesYourCompanyCovers
        );

        formData.append(
          "experience_years",
          values.yearsOfExperienceInTheField
        );

        // Step 4 files
        if (values.commercialRegisterCopy) {
          formData.append(
            "commercial_register",
            values.commercialRegisterCopy
          );
        }

        if (values.professionalLicenseCopy) {
          formData.append("license", values.professionalLicenseCopy);
        }

        if (values.otherSupportiveDocuments) {
          formData.append(
            "other_documents",
            values.otherSupportiveDocuments
          );
        }

        formData.append("agreement", values.iCertify ? 1 : 0);
        formData.append("applicant_name", values.applicantName);

        // API CALL
        const res = await fetch(
          "https://ba7ary.draconiccode.com/public/api/agent-requests",
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw data;
        }

        // SUCCESS
        setSuccessPopup(true);
        formik.resetForm();
        setCurrentStep(1);
      } catch (error) {
        console.error(error);
        alert("حدث خطأ أثناء إرسال الطلب");
      } finally {
        setLoading(false);
      }
    },
  });

  // ================= STEPS =================
  const renderStepForm = () => {
    switch (currentStep) {
      case 1:
        return <Step1Form formik={formik} />;
      case 2:
        return <Step2Form formik={formik} />;
      case 3:
        return <Step3Form formik={formik} />;
      case 4:
        return <Step4Form formik={formik} />;
      default:
        return null;
    }
  };

  // ================= STEP VALIDATION =================
  const validationSchemas = [
    object({
      companyName: string().required(),
      fullAddress: string().required(),
      city: string().required(),
      phone: string().required(),
      email: string().required(),
    }),
    object({
      fullName: string().required(),
      jobPosition: string().required(),
      personalPhone: string().required(),
      personalEmail: string().required(),
    }),
    object({
      experience: string().required(),
    }),
    object({
      commercialRegisterCopy: mixed().required(),
      professionalLicenseCopy: mixed().required(),
      otherSupportiveDocuments: mixed().required(),
      iCertify: boolean().oneOf([true]),
      applicantName: string().required(),
    }),
  ];

  // ================= NEXT =================
  const handleNext = async () => {
    const schema = validationSchemas[currentStep - 1];

    try {
      await schema.validate(formik.values, { abortEarly: false });
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    } catch (err) {
      if (err.inner) {
        const errors = {};
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });

        formik.setErrors(errors);
      }
    }
  };

  return (
    <div>
      <Stepper currentStep={currentStep} />

      <form onSubmit={formik.handleSubmit} className="mt-8">
        {renderStepForm()}

        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() =>
              setCurrentStep((prev) => Math.max(prev - 1, 1))
            }
            className="px-4 py-2 bg-gray-200 rounded"
            disabled={currentStep === 1 || loading}
          >
            {t("previous")}
          </button>

          <button
            type={currentStep === 4 ? "submit" : "button"}
            onClick={currentStep === 4 ? undefined : handleNext}
            disabled={loading}
            className="px-4 py-2 text-white bg-blue-800 rounded"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : currentStep === 4 ? (
              t("finish")
            ) : (
              t("next")
            )}
          </button>
        </div>
      </form>

      {/* ================= SUCCESS POPUP ================= */}
      {successPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-lg text-center w-[320px]">
            <h2 className="text-lg font-bold mb-2">
              تم إرسال الطلب بنجاح 🎉
            </h2>

            <p className="text-gray-600 mb-4">
              سيتم الرد عليك قريباً
            </p>

            <button
              onClick={() => setSuccessPopup(false)}
              className="px-4 py-2 bg-blue-800 text-white rounded"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeOurAgent;